import { defineComponent as _defineComponent } from 'vue'

const __default__ = {
    inheritAttrs: false,
};

import 'jquery-validation';

import { Portal } from '@linusborg/vue-simple-portal';
import $ from 'jquery';
import {
    inject, nextTick, onBeforeUnmount, onMounted, provide, shallowRef,
    unref,
} from 'vue';

import { prepareCustomValidationMethods } from '#public/commons-global';
import { BaseIgnoreList, type ComponentState, useValidator } from '#public_ts/composables';

import { PortalPin, ValidatorInstance } from './injection';
import type { ValidateFormApi } from './validate-form';

/*
Name attribute must be present to validate.
May process built-in attributes as rules:
- required: <InputBox required />
- min <InputBox min="0" />
- max
- minlength
- maxlength
- step
Input types as rules:
- type="email"
- type="number"
- type="url"
- type="date"
- type="range"
May process data attributes as rules (for simple formats):
- data-rule-rangelength="[1, 255]"
- :data-rule-equalto="`#${passwordId}`"
Special jquery data properties (for complex formats):
- :data="{ rules: { IVAorCF: { paese: `#${ids.id_paese}` } } }"
- :data="{ rule-ivaorcf: { paese: `#${ids.id_paese}` } }"
- :data="{ ruleIvaorcf: { paese: `#${ids.id_paese}` } }"
Note: jquery accepts either camelcase or dashed lower case for data attributes, don't include dash into your rule names.
Note2: data-attributes.ts will convert to lower case
External rules object for complex forms:
- <ValidateForm :validator="{ rules }" />
*/


export default /*#__PURE__*/_defineComponent({
  ...__default__,
  __name: 'validate-form',
  props: {
    validator: null,
    state: null,
    pin: { type: Boolean },
    formClass: null,
    ignoreReadonly: { type: Boolean },
    rawSubmit: { type: Boolean }
  },
  emits: ["form-submit"],
  setup(__props: any, { expose, emit }: { emit: ({
    (event: 'form-submit', value: undefined): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
    validator?: JQueryValidation.ValidationOptions;
    state?: ComponentState;
    pin?: boolean;
    formClass?: string;
    ignoreReadonly?: boolean;
    rawSubmit?: boolean;
}


const formElement = shallowRef<HTMLFormElement | undefined>();
const validatorInstance = shallowRef<JQueryValidation.Validator | undefined>();
//eslint-disable-next-line no-spaced-func


//pin both dialog and this form according to props, otherwise pin just dialog and portal this form
const portalPin = inject(PortalPin, undefined);
if(portalPin === undefined) {
    provide(PortalPin, true);
}
//this is for child input controls
provide(ValidatorInstance, validatorInstance);

const publicApi: ValidateFormApi = {
    formElement,
    validateForm() {
        const _validator = unref(validatorInstance);
        if(_validator) {
            return _validator.form();
        }
        return true;
    },
    resetForm(ids?: string[]) {
        const _validator = unref(validatorInstance) as (JQueryValidation.Validator & { resetElements: (elements: JQuery) => void }) | undefined;
        const _formElement = unref(formElement);
        if(_validator && _formElement) {
            if(!ids || !ids.length) {
                //first reset ignored elements, because validator will not reset ignored elements
                //and invalid may become ignored (may become readonly)
                const ignored = $(_formElement)
                    .find('input, select, textarea, [contenteditable]')
                    .filter(`.no-validate,:disabled${props.ignoreReadonly ? ',[readonly]' : ''}`);
                _validator.resetElements(ignored);
                _validator.resetForm();
            } else {
                const nodes = $.map(ids, (i) => document.getElementById(i));
                _validator.resetElements($(nodes));
                $(nodes).removeAttr('aria-invalid');
            }
        }
    },
    reset() {
        const _formElement = unref(formElement);
        if(_formElement) {
            _formElement.reset();
            publicApi.resetForm();
        }
    },
};
expose(publicApi);

function onRawSubmit(event: Event) {
    if(props.rawSubmit) {
        event.preventDefault();
        emit('form-submit', undefined);
    }
}

const onUpdate = props.state?.onUpdate(() => {
    const _validator = unref(validatorInstance);
    if(_validator) {
        _validator.resetForm();
        //this will not reset ignored elements (which became readonly)
    }
});
onMounted(() => {
    //portal requires next tick
    nextTick(() => {
        const _formElement = unref(formElement);
        if(_formElement) {
            prepareCustomValidationMethods();
            const submitHandler: JQueryValidation.ValidationOptions['submitHandler'] = props.rawSubmit
                ? undefined
                : function(form, event) {
                    event?.preventDefault();
                    emit('form-submit', undefined);
                };
            const validatorOptions = useValidator(
                { submitHandler, ignore: props.ignoreReadonly ? `${BaseIgnoreList}[readonly]` : BaseIgnoreList },
                props.validator,
            );
            validatorInstance.value = $(_formElement).validate(validatorOptions);
        }
    });
});
onBeforeUnmount(() => {
    const _validator = unref(validatorInstance);
    if(_validator) {
        _validator.destroy();
    }
    onUpdate?.off();
});

return { __sfc: true,props, formElement, validatorInstance, emit, portalPin, publicApi, onRawSubmit, onUpdate, Portal }
}

})